// src/components/users/UserCard.jsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { Mail, Phone, Book, Calendar, MoreVertical, User } from 'lucide-react';

export const UserCard = ({ user, onUpdate }) => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { put } = useApi();
  const [showActions, setShowActions] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      await put(`/users/${user.id}/status`, { isActive: newStatus });
      onUpdate();
    } catch (error) {
      console.error('Failed to update user status:', error);
    } finally {
      setLoading(false);
      setShowActions(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      student: 'bg-green-100 text-green-800',
      counselor: 'bg-blue-100 text-blue-800',
      admin: 'bg-purple-100 text-purple-800',
      receptionist: 'bg-yellow-100 text-yellow-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {user.fullName || user.username}
            </h3>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
              {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
            </span>
          </div>
          {currentUser.role === 'admin' && (
            <div className="relative">
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <MoreVertical className="h-5 w-5 text-gray-500" />
              </button>
              
              {showActions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                  <button
                    onClick={() => navigate(`/users/${user.id}`)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    View Profile
                  </button>
                  <button
                    onClick={() => navigate(`/users/${user.id}/edit`)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Edit User
                  </button>
                  <button
                    onClick={() => handleStatusChange(!user.isActive)}
                    className={`block w-full text-left px-4 py-2 text-sm ${
                      user.isActive ? 'text-red-600' : 'text-green-600'
                    } hover:bg-gray-100`}
                    disabled={loading}
                  >
                    {user.isActive ? 'Deactivate User' : 'Activate User'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Details */}
        <div className="space-y-2">
          <div className="flex items-center text-sm text-gray-500">
            <Mail className="h-4 w-4 mr-2" />
            {user.email}
          </div>
          {user.phone && (
            <div className="flex items-center text-sm text-gray-500">
              <Phone className="h-4 w-4 mr-2" />
              {user.profile.phone}
            </div>
          )}
          {user.role === 'student' && (
            <>
              <div className="flex items-center text-sm text-gray-500">
                <Book className="h-4 w-4 mr-2" />
                {user.profile?.courseTaken || 'Course taken not specified'}
              </div>
              <div className="flex items-center text-sm text-gray-500">
                <Calendar className="h-4 w-4 mr-2" />
                Year {user.profile?.yearOfStudy}
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex space-x-3">
          <button
            onClick={() => navigate(`/users/${user.id}`)}
            className="flex-1 px-4 py-2 text-sm text-uzima-maroon-500 hover:bg-uzima-maroon-50 rounded-md"
          >
            View Profile
          </button>
          {currentUser.role === 'admin' && user.role === 'student' && (
            <button
              onClick={() => navigate(`/users/${user.id}/sessions`)}
              className="flex-1 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md"
            >
              View Sessions
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
